import EmptyList from '@ifca-root/react-component/src/components/CardList/EmptyList';
import { List, ListItem, ListItemText } from '@material-ui/core';
import React, { useMemo, useState } from 'react';
import { useGetStockBalanceQuery } from 'generated/graphql';
import Loading from '@ifca-root/react-component/src/components/Loading/Loading';
import { format } from 'date-fns';
import { MenuItemInfoDialog } from '../MenuItemInfoDialog';
import { StockMovementDialog } from '../StockMovementDialog';
import WarningIcon from '@material-ui/icons/Warning';

interface StockBalanceTabProps {
	outletID: string;
	startDate: Date | null;
	endDate: Date | null;
	searchQuery: string;
}

export const StockBalanceTab: React.FC<StockBalanceTabProps> = ({
	outletID,
	startDate,
	endDate,
	searchQuery,
}) => {
	const [menuItemInfoDialogOpen, setMenuItemInfoDialogOpen] = useState(false);
	const [stockMovementDialogOpen, setStockMovementDialogOpen] = useState(false);
	const [selectedMenuItemID, setSelectedMenuItemID] = useState<string | null>(null);

	const { data, loading } = useGetStockBalanceQuery({
		fetchPolicy: 'network-only',
		variables: {
			outletID: outletID,
			startDate: startDate || undefined,
			endDate: endDate || undefined,
		},
	});

	const stockItems = data?.getStockBalance || [];

	const handleMenuItemClick = (menuItemID: string) => {
		setSelectedMenuItemID(menuItemID);
		setMenuItemInfoDialogOpen(true);
	};

	const handleCloseMenuItemInfoDialog = () => {
		setMenuItemInfoDialogOpen(false);
		setSelectedMenuItemID(null);
	};

	const handleBalanceClick = (menuItemID: string) => {
		setSelectedMenuItemID(menuItemID);
		setStockMovementDialogOpen(true);
	};

	const handleCloseStockMovementDialog = () => {
		setStockMovementDialogOpen(false);
		setSelectedMenuItemID(null);
	};

	const filteredItems = useMemo(() => {
		if (!searchQuery) return stockItems;
		const query = searchQuery.toLowerCase();
		return stockItems.filter((item: any) => {
			const menuItemName = item.menuItem?.name || '';
			const menuItemDescription = item.menuItem?.description || '';
			return (
				menuItemName.toLowerCase().includes(query) ||
				menuItemDescription.toLowerCase().includes(query)
			);
		});
	}, [stockItems, searchQuery]);

	if (loading) {
		return <Loading />;
	}

	return (
		<>
			<List className="core-list">
				{filteredItems.length === 0 ? (
					<EmptyList
						title="No stock items found"
						subtitle="Stock items will appear here"
					/>
				) : (
					filteredItems.map((item: any) => {
						const stockBalanceQty = parseFloat(item.stockBalanceQty || 0);
						const minQty = item.menuItem?.minQty ? parseFloat(item.menuItem.minQty) : null;
						const isLowStock = minQty !== null && stockBalanceQty < minQty;

						return (
							<ListItem key={item.ID}>
								<ListItemText
									primary={
										<span className="flex-space desc">
										<span
											className="xsTitle"
											style={{
												cursor: item.menuItem?.ID ? 'pointer' : 'default',
												textDecoration: item.menuItem?.ID ? 'underline' : 'none',
											}}
											onClick={() => {
												if (item.menuItem?.ID) {
													handleMenuItemClick(item.menuItem.ID);
												}
											}}
										>
											{item.menuItem?.name || 'N/A'}
										</span>
										<span
											className="desc right-info"
											style={{
												cursor: 'pointer',
												textDecoration: 'underline',
											}}
											onClick={() => {
												if (item.menuItem?.ID) {
													handleBalanceClick(item.menuItem.ID);
												}
											}}
										>
											Balance: {stockBalanceQty.toFixed(4)}
										</span>
										</span>
									}
									secondary={
										<span className="flex-space desc">
											<span className="desc">{format(new Date(item.trxDate), 'dd/MM/yyyy')}</span>
											{isLowStock && (
												<span className="xsTitle color-primary-orange right-info" style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#f44336' }}>
													<WarningIcon style={{ fontSize: '16px', color: '#f44336' }} />
													Low Stock
												</span>
											)}
											{/* <span className="xsTitle color-primary-orange right-info">
										Available: {(item.availableQty || 0).toFixed(4)}
									</span> */}
										</span>
									}
								/>
							</ListItem>
						);
					})
				)}
			</List>
			<MenuItemInfoDialog
				open={menuItemInfoDialogOpen}
				onClose={handleCloseMenuItemInfoDialog}
				menuItemID={selectedMenuItemID}
				outletID={outletID}
			/>
			<StockMovementDialog
				open={stockMovementDialogOpen}
				onClose={handleCloseStockMovementDialog}
				menuItemID={selectedMenuItemID}
				outletID={outletID}
				startDate={startDate}
				endDate={endDate}
			/>
		</>
	);
};

